/***
 *
 *   HEADER USER MENU
 *   Avatar trigger + account menu. Ported from
 *   ui-reference/src/components/layouts/header-user-menu.tsx.
 *
 *   This is where the theme toggle and Sign out now live, as they do in the
 *   reference. Sign out used to be a sidebar item; the reference has it here
 *   only, so the sidebar entry is gone (see docs/ui-refresh/PLAN.md, Phase 3).
 *
 *   The user's display name comes off the `'user'` react-query cache, the same
 *   key AuthProvider already populates, so this shares that request rather than
 *   issuing its own. The API has no per-user avatar image — only organisations
 *   and projects carry a logo — so the initials fallback is the normal case.
 *
 **********/

import { useContext } from 'react';
import { useQuery } from 'react-query';
import { AuthContext } from 'app/auth';
import { useApiContext } from 'app/api';
import { useTranslate } from 'app/translations';
import { UserAvatar, useNavigate, useTheme } from 'components/lib';
import { Menu } from 'components/dropdown/menu';
import Style from './userMenu.tailwind.js';

export function UserMenu() {
    const { _t } = useTranslate();
    const navigate = useNavigate();
    const { callApi } = useApiContext();
    const { signout } = useContext(AuthContext);
    const { isDark, toggleTheme } = useTheme();

    const user = useQuery('user', () => callApi({ url: '/api/user' }));
    const data = user.data?.data;

    const name = [data?.first_name, data?.last_name].filter(Boolean).join(' ');
    const email = data?.email;

    return (
        <Menu
            align="end"
            width="w-56"
            label={_t('navigation.accountMenu')}
            triggerClassName={Style.trigger}
            trigger={<UserAvatar size="sm" name={name || email} bordered />}
            list={[
                // The reference puts the identity in a two-line DropdownMenuLabel.
                // Dropdown's `heading` item renders one line, so the pair is
                // passed as a node.
                {
                    heading: true,
                    label: (
                        <>
                            <span className={Style.name}>{name || email}</span>
                            {name && email && <span className={Style.email}>{email}</span>}
                        </>
                    ),
                },
                { separator: true },
                {
                    label: _t('navigation.profile'),
                    icon: 'user',
                    action: () => navigate('/app/settings/profile'),
                },
                {
                    label: _t('navigation.security'),
                    icon: 'shield',
                    action: () => navigate('/app/settings/password'),
                },
                { separator: true },
                {
                    label: isDark ? _t('theme.lightMode') : _t('theme.darkMode'),
                    icon: isDark ? 'sun' : 'moon',
                    action: toggleTheme,
                },
                { separator: true },
                { label: _t('navigation.signOut'), icon: 'log-out', action: signout },
            ]}
        />
    );
}
